import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Trash2, Users, Pencil, Eye, FileText, UserPlus, Check, X, Receipt, Star, User, Ticket } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PrintColumnsDialog from "@/components/PrintColumnsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = {
  name: "", national_id: "", phone: "", gender: "male", nationality: "",
  campaign_id: "", payment_method: "cash", amount: "",
  registration_kind: "single", private_room: false, override_capacity: false,
};

const emptyCompanion = { name: "", national_id: "", phone: "", gender: "male", nationality: "" };

const fmt = (n) => new Intl.NumberFormat("ar-SA", { maximumFractionDigits: 2 }).format(n || 0);

const COMMERCIAL_PRINT_COLUMNS = [
  { key: "name", label: "الاسم", always: true },
  { key: "national_id", label: "رقم الهوية" },
  { key: "phone", label: "رقم الجوال" },
  { key: "gender", label: "الجنس" },
  { key: "nationality", label: "الجنسية" },
  { key: "campaign", label: "الحملة" },
  { key: "kind", label: "النوع (فردي/عائلة)" },
  { key: "payment_method", label: "طريقة الدفع" },
  { key: "amount", label: "المبلغ" },
  { key: "status", label: "الحالة" },
  { key: "room", label: "الغرفة" },
];

function RoomInput({ value, onSave, testId }) {
  const [val, setVal] = useState(value || "");
  useEffect(() => { setVal(value || ""); }, [value]);
  return (
    <Input
      data-testid={testId}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if ((value || "") !== val) onSave(val); }}
      placeholder="—"
      className="h-8 text-sm"
    />
  );
}

function BeneficiaryRows({ b, idx, campaignName, onView, onEdit, onDelete, onApprove, onReject, onRefresh }) {
  const rowBg = idx % 2 ? "bg-slate-50" : "bg-white";
  const companions = b.companions || [];
  const saveMainRoom = async (room) => {
    try { await api.patch(`/beneficiaries/${b.id}/rooms`, { room_number: room }); toast.success("تم حفظ الغرفة"); onRefresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveCompanionRoom = async (index, room) => {
    const rooms = companions.map((c, i) => (i === index ? room : (c.room_number || null)));
    try { await api.patch(`/beneficiaries/${b.id}/rooms`, { companion_rooms: rooms }); toast.success("تم حفظ الغرفة"); onRefresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <tr className={rowBg}>
        <td className="p-3 font-medium text-emerald-950">{b.name}</td>
        <td className="p-3 tabular-nums text-slate-700">{b.national_id}</td>
        <td className="p-3 tabular-nums text-slate-700">{b.phone}</td>
        <td className="p-3 text-slate-700">{b.gender === "male" ? "ذكر" : "أنثى"}</td>
        <td className="p-3 text-slate-700">{b.nationality}</td>
        <td className="p-3 text-slate-700">{campaignName}</td>
        <td className="p-3">
          {b.registration_kind === "family" ? (
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 inline-flex items-center gap-1">
              <UserPlus className="h-3 w-3" /> عائلة ({companions.length})
            </span>
          ) : (
            <span className="text-slate-500 text-xs">فردي</span>
          )}
          {b.private_room && (
            <div className="mt-1">
              <span data-testid={`private-room-badge-${b.id}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-900 inline-flex items-center gap-1">
                🔒 غرفة خاصة
              </span>
            </div>
          )}
        </td>
        <td className="p-3">
          <span className={`px-2 py-1 rounded-full text-xs font-bold ${b.payment_method === "transfer" ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}>
            {b.payment_method === "transfer" ? "تحويل" : "نقدي"}
          </span>
        </td>
        <td className="p-3 font-bold tabular-nums text-emerald-900">{fmt(b.amount)}</td>
        <td className="p-3">
          {b.status === "confirmed" ? (
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-900">مؤكد</span>
          ) : b.status === "pending" ? (
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-900">قيد المراجعة</span>
          ) : b.status === "failed" ? (
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800">مرفوض</span>
          ) : (
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">—</span>
          )}
        </td>
        <td className="p-3">
          <RoomInput value={b.room_number} onSave={saveMainRoom} testId={`room-main-${b.id}`} />
        </td>
        <td className="p-3">
          <div className="flex gap-1">
            {b.status === "pending" && (
              <>
                {b.receipt_base64 && (
                  <Button size="sm" data-testid={`beneficiary-receipt-${b.id}`}
                    onClick={onView} className="bg-emerald-800 hover:bg-emerald-700 text-white h-8 px-3">
                    <FileText className="h-3.5 w-3.5 ml-1" /> عرض السند
                  </Button>
                )}
                <Button size="sm" variant="ghost" data-testid={`beneficiary-approve-${b.id}`}
                  onClick={onApprove} className="text-emerald-700 hover:bg-emerald-50">
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" data-testid={`beneficiary-reject-${b.id}`}
                  onClick={onReject} className="text-amber-700 hover:bg-amber-50">
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
            {b.status !== "pending" && (b.receipt_base64 || companions.length > 0) && (
              <Button size="sm" variant="ghost" data-testid={`beneficiary-view-${b.id}`}
                onClick={onView} className="text-slate-700 hover:bg-slate-100">
                <Eye className="h-4 w-4" />
              </Button>
            )}
            {b.status === "confirmed" && (
              <>
                <Button size="sm" variant="ghost" data-testid={`beneficiary-voucher-${b.id}`}
                  onClick={() => window.open(`/voucher/${b.id}`, "_blank")}
                  className="text-emerald-800 hover:bg-emerald-50" title="سند قبض">
                  <Receipt className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" data-testid={`beneficiary-ticket-${b.id}`}
                  onClick={async () => {
                    try {
                      let token = b.ticket_token;
                      if (!token) {
                        const { data } = await api.post(`/beneficiaries/${b.id}/ticket`);
                        token = data.ticket_token;
                      }
                      window.open(`/ticket/${token}`, "_blank");
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  className="text-amber-700 hover:bg-amber-50" title="التذكرة الرقمية">
                  <Ticket className="h-4 w-4" />
                </Button>
                {b.rating_token && (
                  <Button size="sm" variant="ghost" data-testid={`beneficiary-rate-link-${b.id}`}
                    onClick={() => {
                      const url = `${window.location.origin}/rate/${b.rating_token}`;
                      navigator.clipboard.writeText(url);
                      toast.success("تم نسخ رابط التقييم");
                    }}
                    className="text-amber-700 hover:bg-amber-50" title="نسخ رابط التقييم">
                    <Star className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
            <Button size="sm" variant="ghost" data-testid={`beneficiary-edit-${b.id}`}
              onClick={onEdit} className="text-emerald-800 hover:bg-emerald-50">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" data-testid={`beneficiary-delete-${b.id}`}
              onClick={onDelete} className="text-red-600 hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </td>
      </tr>
      {companions.map((c, ci) => (
        <tr key={`${b.id}-c-${ci}`} className={`${rowBg} border-r-4 border-amber-400/60`} data-testid={`companion-row-${b.id}-${ci}`}>
          <td className="p-3 ps-8 text-emerald-900 font-medium">
            <span className="inline-flex items-center gap-2">
              <span className="text-amber-600 text-xs">↳</span>
              <span>{c.name}</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">مرافق</span>
            </span>
          </td>
          <td className="p-3 tabular-nums text-slate-700">{c.national_id}</td>
          <td className="p-3 tabular-nums text-slate-700">{c.phone || "—"}</td>
          <td className="p-3 text-slate-700">{c.gender === "male" ? "ذكر" : "أنثى"}</td>
          <td className="p-3 text-slate-700">{c.nationality || "—"}</td>
          <td className="p-3 text-slate-500 text-xs" colSpan={4}>مرافق لـ {b.name}</td>
          <td className="p-3">
            <RoomInput value={c.room_number} onSave={(r) => saveCompanionRoom(ci, r)} testId={`room-comp-${b.id}-${ci}`} />
          </td>
          <td className="p-3"></td>
        </tr>
      ))}
    </>
  );
}

export default function Beneficiaries() {
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [q, setQ] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [companions, setCompanions] = useState([]);
  const [amountEdited, setAmountEdited] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const params = { portal: "commercial" };
      if (q) params.q = q;
      if (campaignFilter && campaignFilter !== "all") params.campaign_id = campaignFilter;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const [b, c] = await Promise.all([
        api.get("/beneficiaries", { params }),
        api.get("/campaigns", { params: { portal: "commercial" } }),
      ]);
      setItems(b.data); setCampaigns(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [q, campaignFilter, statusFilter]);

  const totalAmount = items.reduce((s, x) => s + (Number(x.amount) || 0), 0);

  const openNew = () => { setEditing(null); setForm(empty); setCompanions([]); setAmountEdited(false); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name, national_id: b.national_id, phone: b.phone, gender: b.gender,
      nationality: b.nationality, campaign_id: b.campaign_id,
      payment_method: b.payment_method || "cash", amount: b.amount ?? "",
      registration_kind: b.registration_kind || "single",
      private_room: !!b.private_room, override_capacity: false,
    });
    setCompanions(b.companions || []);
    setAmountEdited(true);
    setOpen(true);
  };

  // Selected campaign helpers for unified flow
  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === form.campaign_id),
    [campaigns, form.campaign_id]
  );
  const unitAmount = Number(selectedCampaign?.amount || 0);
  const privateRoomPrice = Number(selectedCampaign?.private_room_price || 0);
  const peopleCount = 1 + (form.registration_kind === "family" ? companions.length : 0);

  // Server-mirroring private-room logic (visibility + forced)
  let showPrivateRoom = false;
  let privateRoomForced = false;
  if (privateRoomPrice > 0) {
    if (peopleCount === 1 && form.gender === "female") {
      showPrivateRoom = true; privateRoomForced = true;
    } else if (peopleCount === 2) {
      const gc = companions[0]?.gender || "male";
      showPrivateRoom = true;
      privateRoomForced = gc === "female";
    }
  }
  const privateRoomActive = showPrivateRoom && (privateRoomForced || form.private_room);
  const autoTotal = unitAmount * peopleCount + (privateRoomActive ? privateRoomPrice : 0);

  // Auto-update amount when auto-calc drivers change (unless user manually edited)
  useEffect(() => {
    if (!editing && !amountEdited && selectedCampaign) {
      setForm((f) => ({ ...f, amount: autoTotal ? String(autoTotal) : "" }));
    }
  }, [autoTotal, editing, amountEdited, selectedCampaign?.id]);

  // Capacity live-check
  const capacity = Number(selectedCampaign?.capacity || 0);
  const currentOccupancy = useMemo(() => {
    if (!capacity || !selectedCampaign) return 0;
    return items
      .filter((b) => b.campaign_id === selectedCampaign.id && (b.status === "confirmed" || b.status === "pending"))
      .reduce((s, b) => s + 1 + ((b.companions || []).length), 0);
  }, [items, selectedCampaign, capacity]);
  const needed = peopleCount;
  const willExceedCapacity = capacity > 0 && (currentOccupancy + needed) > capacity;

  const updateCompanion = (i, key, val) => {
    setCompanions((prev) => {
      const c = [...prev]; c[i] = { ...c[i], [key]: val }; return c;
    });
  };
  const setCompanionCount = (n) => {
    n = Math.max(0, Math.min(10, n));
    setCompanions((prev) => {
      if (n === prev.length) return prev;
      if (n > prev.length) return [...prev, ...Array(n - prev.length).fill(null).map(() => ({ ...emptyCompanion }))];
      return prev.slice(0, n);
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (!form.campaign_id) { toast.error("اختر الحملة أولاً"); return; }
      if (form.registration_kind === "family") {
        for (let i = 0; i < companions.length; i++) {
          const c = companions[i];
          if (!c.name || !c.national_id) {
            toast.error(`أكمل بيانات المرافق رقم ${i + 1} (الاسم والهوية)`);
            return;
          }
        }
      }
      const payload = {
        ...form,
        portal: "commercial",
        amount: form.amount === "" ? null : Number(form.amount),
        companions: form.registration_kind === "family" ? companions : [],
      };
      if (editing) {
        // On edit, keep legacy patch shape (limited fields)
        const patch = {
          name: form.name, national_id: form.national_id, phone: form.phone,
          gender: form.gender, nationality: form.nationality,
          campaign_id: form.campaign_id, payment_method: form.payment_method,
          amount: form.amount === "" ? 0 : Number(form.amount),
        };
        await api.patch(`/beneficiaries/${editing.id}`, patch);
        toast.success("تم التعديل");
        setOpen(false); setForm(empty); setCompanions([]); setAmountEdited(false); setEditing(null); load();
      } else {
        const { data } = await api.post("/beneficiaries", payload);
        toast.success("تمت الإضافة — التذكرة جاهزة");
        setOpen(false); setForm(empty); setCompanions([]); setAmountEdited(false); setEditing(null); load();
        if (data?.ticket_token) {
          // Auto-open the digital ticket page so staff can immediately share it with the beneficiary
          window.open(`/ticket/${data.ticket_token}`, "_blank");
        }
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("هل تريد حذف هذا المستفيد؟")) return;
    try { await api.delete(`/beneficiaries/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/beneficiaries/${id}/approve`);
      toast.success("تم قبول التسجيل — التذكرة جاهزة");
      load();
      if (data?.ticket_token) {
        window.open(`/ticket/${data.ticket_token}`, "_blank");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reject = async (id) => {
    const note = window.prompt("سبب الرفض (اختياري):", "");
    if (note === null) return;
    try { await api.post(`/beneficiaries/${id}/reject`, { note }); toast.success("تم رفض التسجيل"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const campaignName = useMemo(() => {
    const map = new Map(campaigns.map((c) => [c.id, `#${c.number} ${c.name}`]));
    return (id) => map.get(id) || "—";
  }, [campaigns]);

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        title="مستفيدو البوابة التجارية"
        subtitle={`العدد: ${items.length}${totalAmount > 0 ? ` — إجمالي المبالغ: ${fmt(totalAmount)} ر.س` : ""}`}
        actions={
          <>
            <PrintColumnsDialog
              prefsKey="commercial_beneficiaries"
              columns={COMMERCIAL_PRINT_COLUMNS}
              buildUrl={(cols) => {
                const qs = new URLSearchParams();
                qs.set("cols", cols.join(","));
                if (campaignFilter && campaignFilter !== "all") qs.set("campaign", campaignFilter);
                if (statusFilter && statusFilter !== "all") qs.set("status", statusFilter);
                if (q) qs.set("q", q);
                return `/print/commercial/beneficiaries?${qs.toString()}`;
              }}
              testIdPrefix="beneficiaries-print"
            />
            <Button data-testid="beneficiary-add-btn" onClick={openNew} className="bg-emerald-900 hover:bg-emerald-800 text-white">
              <Plus className="h-4 w-4 ml-2" /> مستفيد جديد
            </Button>
          </>
        }
      />

      <Card className="p-4 mb-4 border-emerald-900/10">
        <div className="grid md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="beneficiary-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث بالاسم، الهوية، أو الجوال" className="pr-9" />
          </div>
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger data-testid="beneficiary-campaign-filter"><SelectValue placeholder="فلترة بالحملة" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع الحملات</SelectItem>
              {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>#{c.number} {c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-emerald-900/10 overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-16 text-center">
            <Users className="h-10 w-10 text-emerald-900/30 mx-auto mb-3" />
            <p className="text-slate-500">لا يوجد مستفيدون. ابدأ بإضافة أول مستفيد.</p>
          </div>
        ) : (
          <table className="w-full text-right min-w-[1000px]" data-testid="beneficiaries-table">
            <thead className="bg-emerald-950 text-white">
              <tr>
                <th className="p-3 font-heading">الاسم</th>
                <th className="p-3 font-heading">رقم الهوية</th>
                <th className="p-3 font-heading">الجوال</th>
                <th className="p-3 font-heading">الجنس</th>
                <th className="p-3 font-heading">الجنسية</th>
                <th className="p-3 font-heading">الحملة</th>
                <th className="p-3 font-heading">النوع</th>
                <th className="p-3 font-heading">الدفع</th>
                <th className="p-3 font-heading">المبلغ</th>
                <th className="p-3 font-heading">الحالة</th>
                <th className="p-3 font-heading w-32">الغرفة</th>
                <th className="p-3 font-heading w-32">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b, i) => (
                <BeneficiaryRows
                  key={b.id}
                  b={b}
                  idx={i}
                  campaignName={campaignName(b.campaign_id)}
                  onView={() => setDetail(b)}
                  onEdit={() => openEdit(b)}
                  onDelete={() => remove(b.id)}
                  onApprove={() => approve(b.id)}
                  onReject={() => reject(b.id)}
                  onRefresh={load}
                />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent dir="rtl" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-right">تفاصيل التسجيل</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">الاسم:</span> <b>{detail.name}</b></div>
                <div><span className="text-slate-500">الهوية:</span> <b>{detail.national_id}</b></div>
                <div><span className="text-slate-500">النوع:</span> <b>{detail.registration_kind === "family" ? "عائلة" : "فردي"}</b></div>
                <div><span className="text-slate-500">الحالة:</span> <b>{detail.status === "confirmed" ? "مؤكد" : detail.status}</b></div>
              </div>
              {detail.companions && detail.companions.length > 0 && (
                <div>
                  <h4 className="font-heading font-bold text-emerald-950 mb-2">المرافقون ({detail.companions.length})</h4>
                  <table className="w-full text-right text-sm border">
                    <thead className="bg-slate-100"><tr>
                      <th className="p-2">الاسم</th><th className="p-2">الهوية</th>
                      <th className="p-2">الجنس</th><th className="p-2">الجوال</th>
                    </tr></thead>
                    <tbody>
                      {detail.companions.map((c, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{c.name}</td>
                          <td className="p-2 tabular-nums">{c.national_id}</td>
                          <td className="p-2">{c.gender === "male" ? "ذكر" : "أنثى"}</td>
                          <td className="p-2 tabular-nums">{c.phone || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {detail.receipt_base64 && (
                <div>
                  <h4 className="font-heading font-bold text-emerald-950 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" /> سند التحويل
                  </h4>
                  {detail.receipt_base64.startsWith("data:application/pdf") ? (
                    <object
                      data={detail.receipt_base64}
                      type="application/pdf"
                      className="w-full h-[500px] rounded-lg border border-emerald-900/10"
                    >
                      <a href={detail.receipt_base64} target="_blank" rel="noreferrer" download={`سند-${detail.name}.pdf`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-900 text-white rounded-lg">
                        <FileText className="h-4 w-4" /> فتح ملف PDF في نافذة جديدة
                      </a>
                    </object>
                  ) : (
                    <img src={detail.receipt_base64} alt="سند التحويل" className="max-w-full max-h-[500px] rounded-lg border object-contain" />
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-right">
            {editing ? "تعديل مستفيد" : "مستفيد جديد (تسجيل حضوري)"}
          </DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-5">
            {/* Campaign */}
            <div>
              <Label>الحملة *</Label>
              <Select value={form.campaign_id} onValueChange={(v) => { setForm({ ...form, campaign_id: v }); setAmountEdited(false); }}>
                <SelectTrigger data-testid="ben-form-campaign"><SelectValue placeholder="اختر الحملة" /></SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>#{c.number} {c.name}</SelectItem>))}
                </SelectContent>
              </Select>
              {selectedCampaign && (
                <div className="mt-1 text-xs text-slate-600 flex gap-3 flex-wrap">
                  <span>سعر الفرد: <b className="text-emerald-800">{fmt(unitAmount)} ر.س</b></span>
                  {privateRoomPrice > 0 && <span>غرفة خاصة: <b className="text-emerald-800">{fmt(privateRoomPrice)} ر.س</b></span>}
                  {capacity > 0 && <span>السعة: <b className="text-emerald-800">{currentOccupancy}/{capacity}</b></span>}
                </div>
              )}
            </div>

            {/* Registration kind */}
            {!editing && (
              <div>
                <Label className="mb-2 block">نوع التسجيل *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" data-testid="ben-kind-single"
                    onClick={() => { setForm({ ...form, registration_kind: "single" }); setCompanions([]); setAmountEdited(false); }}
                    className={`p-4 rounded-xl border-2 text-right transition-all ${form.registration_kind === "single" ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}>
                    <User className={`h-6 w-6 mb-1 ${form.registration_kind === "single" ? "text-emerald-700" : "text-slate-400"}`} />
                    <div className="font-bold text-emerald-950 text-sm">فردي</div>
                  </button>
                  <button type="button" data-testid="ben-kind-family"
                    onClick={() => { setForm({ ...form, registration_kind: "family" }); setAmountEdited(false); }}
                    className={`p-4 rounded-xl border-2 text-right transition-all ${form.registration_kind === "family" ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}>
                    <Users className={`h-6 w-6 mb-1 ${form.registration_kind === "family" ? "text-emerald-700" : "text-slate-400"}`} />
                    <div className="font-bold text-emerald-950 text-sm">عائلة (مع مرافقين)</div>
                  </button>
                </div>
              </div>
            )}

            {/* Main beneficiary data */}
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>الاسم الكامل *</Label>
                <Input data-testid="ben-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div><Label>رقم الهوية *</Label>
                <Input data-testid="ben-form-id" value={form.national_id} onChange={(e) => setForm({ ...form, national_id: e.target.value })} required />
              </div>
              <div><Label>رقم الجوال *</Label>
                <Input data-testid="ben-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
              </div>
              <div><Label>الجنسية *</Label>
                <Input data-testid="ben-form-nationality" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} required />
              </div>
              <div><Label>الجنس</Label>
                <Select value={form.gender} onValueChange={(v) => { setForm({ ...form, gender: v }); setAmountEdited(false); }}>
                  <SelectTrigger data-testid="ben-form-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">ذكر</SelectItem>
                    <SelectItem value="female">أنثى</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>طريقة الدفع</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="ben-form-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">نقدي</SelectItem>
                    <SelectItem value="transfer">تحويل</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Companion counter + forms */}
            {!editing && form.registration_kind === "family" && (
              <Card className="p-4 bg-emerald-50/60 border-emerald-200">
                <Label className="text-emerald-900 font-bold mb-3 block">كم عدد المرافقين؟</Label>
                <div className="flex items-center gap-3 mb-4">
                  <Button type="button" data-testid="ben-companion-minus"
                    onClick={() => setCompanionCount(companions.length - 1)}
                    disabled={companions.length === 0}
                    className="h-11 w-11 bg-emerald-900 hover:bg-emerald-800 text-white text-xl p-0 disabled:opacity-30">−</Button>
                  <div data-testid="ben-companion-count"
                    className="flex-1 h-11 flex items-center justify-center bg-white rounded-lg border-2 border-emerald-200 font-heading text-xl font-black text-emerald-900 tabular-nums">
                    {companions.length}
                  </div>
                  <Button type="button" data-testid="ben-companion-plus"
                    onClick={() => setCompanionCount(companions.length + 1)}
                    disabled={companions.length >= 10}
                    className="h-11 w-11 bg-emerald-900 hover:bg-emerald-800 text-white text-xl p-0 disabled:opacity-30">+</Button>
                </div>

                <div className="space-y-3">
                  {companions.map((c, i) => (
                    <Card key={i} className="p-3 bg-white border-emerald-900/10">
                      <div className="font-bold text-emerald-950 mb-2 text-sm">مرافق #{i + 1}</div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">الاسم *</Label>
                          <Input data-testid={`ben-comp-${i}-name`} value={c.name} onChange={(e) => updateCompanion(i, "name", e.target.value)} />
                        </div>
                        <div><Label className="text-xs">رقم الهوية *</Label>
                          <Input data-testid={`ben-comp-${i}-id`} value={c.national_id} onChange={(e) => updateCompanion(i, "national_id", e.target.value)} />
                        </div>
                        <div><Label className="text-xs">الجوال</Label>
                          <Input value={c.phone} onChange={(e) => updateCompanion(i, "phone", e.target.value)} />
                        </div>
                        <div><Label className="text-xs">الجنسية</Label>
                          <Input value={c.nationality} onChange={(e) => updateCompanion(i, "nationality", e.target.value)} />
                        </div>
                        <div><Label className="text-xs">الجنس</Label>
                          <Select value={c.gender} onValueChange={(v) => { updateCompanion(i, "gender", v); setAmountEdited(false); }}>
                            <SelectTrigger data-testid={`ben-comp-${i}-gender`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">ذكر</SelectItem>
                              <SelectItem value="female">أنثى</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>
            )}

            {/* Private room */}
            {!editing && showPrivateRoom && (
              <Card className="p-4 bg-amber-50 border-amber-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox"
                    data-testid="ben-private-room"
                    checked={privateRoomActive}
                    disabled={privateRoomForced}
                    onChange={(e) => { setForm({ ...form, private_room: e.target.checked }); setAmountEdited(false); }}
                    className="mt-1 h-5 w-5 accent-emerald-700"
                  />
                  <div>
                    <div className="text-emerald-950 font-bold text-sm">
                      غرفة خاصة (+{fmt(privateRoomPrice)} ر.س)
                    </div>
                    {privateRoomForced && (
                      <div data-testid="ben-private-room-forced" className="text-xs text-amber-800 mt-1">
                        الغرفة الخاصة إلزامية لضمان الخصوصية.
                      </div>
                    )}
                  </div>
                </label>
              </Card>
            )}

            {/* Capacity warning + override */}
            {!editing && willExceedCapacity && (
              <Card className="p-4 bg-red-50 border-red-200" data-testid="ben-capacity-warning">
                <div className="text-red-800 font-bold text-sm mb-2">
                  ⚠️ اكتمل عدد المقاعد ({currentOccupancy}/{capacity}) — تسجيل {needed} سيتجاوز الحد.
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" data-testid="ben-override-capacity"
                    checked={form.override_capacity}
                    onChange={(e) => setForm({ ...form, override_capacity: e.target.checked })}
                    className="h-4 w-4 accent-red-700" />
                  <span className="text-red-900 font-bold">تجاوز السعة يدوياً (حالة استثنائية)</span>
                </label>
              </Card>
            )}

            {/* Amount */}
            <div>
              <Label>المبلغ (ر.س) {!editing && <span className="text-xs text-slate-500">(محسوب تلقائياً — يمكن تعديله)</span>}</Label>
              <Input type="number" step="0.01" data-testid="ben-form-amount" value={form.amount}
                onChange={(e) => { setAmountEdited(true); setForm({ ...form, amount: e.target.value }); }} required />
              {!editing && selectedCampaign && (
                <div className="text-xs text-slate-500 mt-1">
                  {unitAmount > 0 ? `(${fmt(unitAmount)} × ${peopleCount}${privateRoomActive ? ` + ${fmt(privateRoomPrice)} غرفة خاصة` : ""} = ${fmt(autoTotal)} ر.س)` : ""}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="submit" data-testid="ben-form-save"
                disabled={willExceedCapacity && !form.override_capacity}
                className="bg-emerald-900 hover:bg-emerald-800 text-white">
                {editing ? "تحديث" : "إضافة"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
